import BrokeredFolderPicker from 'components/Links/Brokered/BrokeredFolderPicker';
import SharepointFolderPicker from 'components/SharePoint/SharepointFolderPicker';
import { isBrokeredProvider } from 'models/documentProvider';
import ResourceList from 'models/resourceList';
import { IDriveItem } from 'services/Graph/SharepointInterfaces';

interface IOutputFolderPickerProps {
  list: ResourceList;
  onSelect: (folderDriveItemId: string | undefined, folderName: string | undefined) => void;
  // Called on cancel and after a select: the provider's picker closes itself.
  onClose: () => void;
}

/** True when a folder can be picked inside the category: the picker needs its drive, or its connection. */
export const canPickOutputFolder = (list: ResourceList | undefined): boolean => {
  if (!list) return false;
  if (isBrokeredProvider(list)) return list.connectionId !== undefined;

  return !!list.spDriveId && !!list.spDriveItemId;
};

/**
 * A folder inside a category, with the picker of the category's provider, starting at the category's root and
 * staying below it. Picking the root itself answers no folder: the files then go into the category itself.
 */
const OutputFolderPicker = (props: IOutputFolderPickerProps) => {
  const list = props.list;

  const select = (folderId: string | undefined, name: string | undefined) => {
    const id = folderId === list.spDriveItemId ? undefined : folderId;
    props.onSelect(id, id ? name : undefined);
  };

  if (!canPickOutputFolder(list)) return null;

  if (isBrokeredProvider(list) && list.connectionId !== undefined) {
    return (
      <BrokeredFolderPicker
        isOpen={true}
        connectionId={list.connectionId}
        containerId={list.spDriveId}
        rootFolderId={list.spDriveItemId}
        onSelect={(_containerId: string, folderId: string | undefined, path: string) => select(folderId, path)}
        close={props.onClose}
      />
    );
  }

  return (
    <SharepointFolderPicker
      isOpen={true}
      parentFolder={
        list.spDriveId && list.spDriveItemId
          ? { driveId: list.spDriveId, driveItemId: list.spDriveItemId, tenantId: list.altTenantId }
          : undefined
      }
      onSelect={(item: IDriveItem) => select(item.id ?? undefined, item.name ?? undefined)}
      close={props.onClose}
    />
  );
};

export default OutputFolderPicker;
